import React, { useEffect, useState } from "react";
import { Badge, Button, Card, CardBody, Col, Container, Row } from "reactstrap";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import AdminHeader from "components/Headers/AdminHeader";
import DetailsHeader from "components/CommonComponents/DetailsHeader";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import { getInstructorAssessmentReportDetails } from "action/report";
import { instructorAssessmentReportDetails } from "constants/report";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";

const INITIAL_FILTERS = {
  traineeName: "",
  traineeEmail: "",
  traineeMobile: "",
  attemptStatus: "",
  resultStatus: "",
};

const ATTEMPT_STATUS_OPTIONS = [
  { value: "ATTEMPTED", label: "Attempted" },
  { value: "NOT_ATTEMPTED", label: "Not Attempted" },
];

const RESULT_STATUS_OPTIONS = [
  { value: "PASS", label: "Pass" },
  { value: "FAIL", label: "Fail" },
];

function AssessmentReportDetails() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { assessmentId: encodedAssessmentId, batchId: encodedBatchId } =
    useParams();

  const assessmentId = urlDecodedId(encodedAssessmentId);
  const batchId = urlDecodedId(encodedBatchId);

  const assessmentReportDetailsData = useSelector(
    (state) => state.report.instructorAssessmentReportDetails,
  );

  const [pageNum, setPageNum] = useState(1);
  const [filterState, setFilterState] = useState({
    ...INITIAL_FILTERS,
    filterParams: [],
  });
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
  });

  useEffect(() => {
    if (!assessmentId || !batchId) {
      return;
    }

    getInstructorAssessmentReportDetails(
      dispatch,
      assessmentId,
      batchId,
      filterState.filterParams,
      pageNum,
    );

    return () => {
      dispatch({
        type: instructorAssessmentReportDetails,
        payload: {},
      });
    };
  }, [assessmentId, batchId, dispatch, filterState.filterParams, pageNum]);

  useEffect(() => {
    if (assessmentReportDetailsData?.code === 200) {
      const rows = assessmentReportDetailsData?.data?.trainees || [];

      setListState({
        isLoading: false,
        error: "",
        noRecords: rows.length === 0,
      });
    } else if (
      assessmentReportDetailsData?.code ||
      assessmentReportDetailsData?.message
    ) {
      setListState({
        isLoading: false,
        error:
          assessmentReportDetailsData?.message ||
          "Failed to load assessment details.",
        noRecords: false,
      });
    }
  }, [assessmentReportDetailsData]);

  const buildFilterParams = () => {
    const filters = [];

    if (filterState.traineeName.trim()) {
      filters.push({
        field: "TraineeName",
        condition: "like",
        filterValues: [filterState.traineeName.trim()],
      });
    }

    if (filterState.traineeEmail.trim()) {
      filters.push({
        field: "Email",
        condition: "like",
        filterValues: [filterState.traineeEmail.trim()],
      });
    }

    if (filterState.traineeMobile.trim()) {
      filters.push({
        field: "Mobile",
        condition: "like",
        filterValues: [filterState.traineeMobile.trim()],
      });
    }

    if (filterState.attemptStatus) {
      filters.push({
        field: "AttemptStatus",
        condition: "eq",
        filterValues: [filterState.attemptStatus],
      });
    }

    if (filterState.resultStatus) {
      filters.push({
        field: "ResultStatus",
        condition: "eq",
        filterValues: [filterState.resultStatus],
      });
    }

    return filters;
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setFilterState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));
      setPageNum(1);
    }
  };

  const handleClear = () => {
    setFilterState({
      ...INITIAL_FILTERS,
      filterParams: [],
    });
    setPageNum(1);
  };

  const handleFilterChange = (fieldName) => (selected) => {
    const value = selected ? selected.value : "";

    setFilterState((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const tableData = (assessmentReportDetailsData?.data?.trainees || []).map(
    (trainee) => ({
      id: trainee.traineeID,
      traineeName: trainee.traineeName,
      traineeEmail: trainee.traineeEmail,
      traineeMobile: trainee.traineeMobile,
      attemptStatus:
        trainee.attempt_status === "ATTEMPTED"
          ? "Attempted"
          : trainee.attempt_status === "NOT_ATTEMPTED"
            ? "Not Attempted"
            : "-",
      resultStatus:
        trainee.result_status === "PASS"
          ? "Pass"
          : trainee.result_status === "FAIL"
            ? "Fail"
            : "-",
    }),
  );

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 70,
      maxWidth: 80,
      centerAlign: true,
      Cell: ({ row }) =>
        (pageNum - 1) * assessmentReportDetailsData?.data?.noOfRecordsPerPage ||
        DEFAULT_RECORDS_PER_PAGE + row.index + 1,
    },
    {
      Header: "Trainee Name",
      accessor: "traineeName",
      minWidth: 180,
      maxWidth: 180,
      Cell: ({ value }) => <div className="text-wrapper">{value || "—"}</div>,
    },
    {
      Header: "Trainee Email",
      accessor: "traineeEmail",
      minWidth: 220,
      maxWidth: 220,
      Cell: ({ value }) => <div className="text-wrapper">{value || "—"}</div>,
    },
    {
      Header: "Trainee Mobile",
      accessor: "traineeMobile",
      minWidth: 170,
      maxWidth: 170,
      Cell: ({ value }) => <div className="text-wrapper">{value || "—"}</div>,
    },
    {
      Header: "Attempt Status",
      accessor: "attemptStatus",
      minWidth: 180,
      maxWidth: 180,
      centerAlign: true,
      Cell: ({ value }) => (
        <Badge color={value === "Attempted" ? "success" : "warning"} pill>
          {value || "-"}
        </Badge>
      ),
    },
    {
      Header: "Result Status",
      accessor: "resultStatus",
      minWidth: 170,
      maxWidth: 170,
      centerAlign: true,
      Cell: ({ value }) => (
        <Badge
          color={
            value === "Pass"
              ? "success"
              : value === "Fail"
                ? "danger"
                : "secondary"
          }
          pill
        >
          {value || "-"}
        </Badge>
      ),
    },
  ];

  if (!assessmentId || !batchId) {
    return (
      <>
        <AdminHeader />
        <Container fluid className="mt--6">
          <Card>
            <CardBody>
              <h3 className="mb-0 text-default">
                Assessment information is missing.
              </h3>
            </CardBody>
          </Card>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminHeader />

      <Container fluid className="mt--6 course-layout">
        <Row>
          <Col md="12">
            <DetailsHeader
              onBack={() => navigate("/instructor/reports/assessment-report")}
              backTooltip="Back to Assessment Reports"
              title={assessmentReportDetailsData?.data?.assessmentName}
              showEdit={false}
              chips={[
                {
                  id: "courseName",
                  icon: "fa fa-book",
                  name: "Course",
                  value: assessmentReportDetailsData?.data?.courseName,
                  tooltip: assessmentReportDetailsData?.data?.courseName,
                },
                {
                  id: "batchName",
                  icon: "fa fa-calendar-check",
                  name: "Batch",
                  value: assessmentReportDetailsData?.data?.batchName,
                  tooltip: assessmentReportDetailsData?.data?.batchName,
                },
              ].filter((chip) => chip.value)}
            />

            <Card className="mt-3">
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <input
                      type="text"
                      name="traineeName"
                      className="form-control mr-2 mb-2 filter-input-large"
                      placeholder="Search by trainee name"
                      value={filterState.traineeName}
                      onChange={(event) =>
                        setFilterState((prev) => ({
                          ...prev,
                          traineeName: event.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <input
                      type="text"
                      name="traineeEmail"
                      className="form-control mr-2 mb-2 filter-input-large"
                      placeholder="Search by trainee email"
                      value={filterState.traineeEmail}
                      onChange={(event) =>
                        setFilterState((prev) => ({
                          ...prev,
                          traineeEmail: event.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <input
                      type="text"
                      name="traineeMobile"
                      className="form-control mr-2 mb-2 filter-input-large"
                      placeholder="Search by trainee mobile"
                      value={filterState.traineeMobile}
                      onChange={(event) =>
                        setFilterState((prev) => ({
                          ...prev,
                          traineeMobile: event.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      name="attemptStatus"
                      className="mr-2 mb-2 filter-select"
                      classNamePrefix="react-select"
                      options={ATTEMPT_STATUS_OPTIONS}
                      placeholder="Select attempt status"
                      value={
                        ATTEMPT_STATUS_OPTIONS.find(
                          (option) =>
                            option.value === filterState.attemptStatus,
                        ) || null
                      }
                      onChange={handleFilterChange("attemptStatus")}
                      isClearable
                      styles={fixedWidthCustomStyle()}
                    />

                    <Select
                      name="resultStatus"
                      className="mr-2 mb-2 filter-select"
                      classNamePrefix="react-select"
                      options={RESULT_STATUS_OPTIONS}
                      placeholder="Select result status"
                      value={
                        RESULT_STATUS_OPTIONS.find(
                          (option) => option.value === filterState.resultStatus,
                        ) || null
                      }
                      onChange={handleFilterChange("resultStatus")}
                      isClearable
                      styles={fixedWidthCustomStyle()}
                    />

                    <Button
                      color="default"
                      onClick={handleSearch}
                      className="d-flex align-items-center mb-2 filter-btn btn-deep-blue"
                    >
                      <i className="fa fa-search mr-1" />
                      Search
                    </Button>

                    {filterState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="mb-2 filter-btn btn-simple btn-deep-blue"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {tableData.length > 0 && (
                    <>
                      {filterState.filterParams.length > 0 ? (
                        <span className="total-records">
                          Showing {assessmentReportDetailsData?.data?.totalFilteredRecords} of{" "}
                          {assessmentReportDetailsData?.data?.totalRecords}{" "}
                          Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records :{" "}
                          {assessmentReportDetailsData?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  )}
                </div>

                {listState.error ? (
                  <div className="loaderContainer empty-style">
                    <div className="empty-icon">
                      <i className="fa-solid fa-circle-info" />
                    </div>
                    <span>{listState.error}</span>
                  </div>
                ) : listState.noRecords || tableData.length === 0 ? (
                  <div className="loaderContainer empty-style">
                    <div className="empty-icon">
                      <i className="fa-solid fa-circle-info" />
                    </div>
                    <span>No trainee records found.</span>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      columns={columns}
                      data={tableData}
                      defaultPageSize={
                        assessmentReportDetailsData?.data?.noOfRecordsPerPage ||
                        DEFAULT_RECORDS_PER_PAGE
                      }
                      activePage={pageNum}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        assessmentReportDetailsData?.data
                          ?.totalFilteredRecords /
                          assessmentReportDetailsData?.data
                            ?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE,
                      )}
                      showPaginationBottom={true}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default AssessmentReportDetails;
